import { useEffect, useState } from "react";

/**
 * A tabbed page's active section, kept in the URL hash (#1681).
 *
 * `/settings#household` solved this first: reload, back/forward and shared
 * links all land on the section they name, without the app router having to
 * model a section as an object (it is not one; `app/detailRoutes.ts` is for
 * objects within a tab). Finance now uses the same rule, and any later tabbed
 * page should too — the point of lifting it here is that there is ONE hash
 * behaviour, not a third.
 *
 * The rules, shared by every caller:
 * - an empty or unknown hash is the fallback section, and the hash is dropped;
 * - a known hash is matched case-insensitively and normalised to its id;
 * - the hash is written with `replaceState`, so clicking through sections adds
 *   no history entries and Back leaves the page, exactly as Settings does;
 * - `hashchange` (a typed URL, a same-page link) is followed. `pushState`
 *   navigation does not fire it, and does not need to: a link into the page
 *   from another tab remounts the view, which reads the hash on mount.
 */

/** The section a hash names, or null for an empty or unknown one. */
export function sectionFromHash<T extends string>(ids: readonly T[], hash: string): T | null {
  const raw = hash.replace(/^#/, "").trim().toLowerCase();
  if (!raw) return null;
  return ids.find((id) => id.toLowerCase() === raw) ?? null;
}

/** The path for a section: the fallback is hashless, everything else is `#id`. */
export function pathForSection<T extends string>(path: string, section: T, fallback: T): string {
  return section === fallback ? path : `${path}#${section}`;
}

export type HashSectionOptions<T extends string> = {
  /**
   * A section to open when the hash names none, before falling back — Settings
   * opens Google on the OAuth return, which comes back hashless.
   */
  initial?: () => T | null;
  /**
   * Whether a section may be shown to this reader. A hash naming a hidden
   * section (an owner-only one opened by a member) is treated as unknown.
   */
  isVisible?: (id: T) => boolean;
};

/**
 * Active section state backed by the URL hash. Returns the section to show
 * and the setter a tab button calls. Effects do not run under the unit
 * harness (renderToStaticMarkup), so the initial read is synchronous and the
 * markup a test sees is the section the hash named.
 */
export function useHashSection<T extends string>(
  ids: readonly T[],
  fallback: T,
  options: HashSectionOptions<T> = {}
): [T, (id: T) => void] {
  const { initial, isVisible } = options;
  const [section, setSection] = useState<T>(() =>
    sectionFromHash(ids, window.location.hash) ?? initial?.() ?? fallback
  );
  const active = section !== fallback && isVisible && !isVisible(section) ? fallback : section;

  useEffect(() => {
    function onHashChange() {
      const next = sectionFromHash(ids, window.location.hash);
      // A typed unknown hash lands on the fallback and is dropped here as well:
      // when the fallback was already showing, the state does not change, so
      // the write effect below would not run and the stale hash would stay.
      if (next === null && window.location.hash) {
        window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
      }
      setSection(next ?? fallback);
    }
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [fallback, ids]);

  // Keep the URL hash honest with what is actually shown: write #section for
  // non-default sections, drop the hash for the fallback, and clear a stale or
  // not-visible hash. replaceState does not fire hashchange, so no loop.
  useEffect(() => {
    const base = `${window.location.pathname}${window.location.search}`;
    if (active === fallback) {
      if (window.location.hash) window.history.replaceState(null, "", base);
    } else if (window.location.hash !== `#${active}`) {
      // Also rewrites a differently-cased hash (`#Budgets`) to the id, so the
      // address a person copies is the one canonical spelling.
      window.history.replaceState(null, "", `${base}#${active}`);
    }
  }, [active, fallback, ids]);

  return [active, setSection];
}
